import React, { useEffect, useState } from 'react';
import { XIcon, TrashIcon, SearchIcon } from './Icons';
import { useAuth } from '../contexts/AuthContext';
import { stickerTextStyle } from './StickerText';
import { fetchIdeas, deleteIdea } from '../api';

interface Idea {
    id: string;
    text: string;
    reply: string;
    category: string;
    timestamp: string;
}

interface IdeaBankModalProps {
    isOpen: boolean;
    onClose: () => void;
    username: string;
}

export const IdeaBankModal = ({ isOpen, onClose, username }: IdeaBankModalProps) => {
    const { usersData } = useAuth();
    const [ideas, setIdeas] = useState<Idea[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen && username) {
            loadIdeas();
        }
    }, [isOpen, username]);

    const loadIdeas = async () => {
        setLoading(true);
        try {
            const data = await fetchIdeas(username);
            setIdeas(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error("Failed to load ideas:", e);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (ideaId: string) => {
        try {
            await deleteIdea(username, ideaId);
            setIdeas(prev => prev.filter(idea => idea.id !== ideaId));
        } catch (e) {
            console.error("Failed to delete idea:", e);
        }
    };

    if (!isOpen) return null;

    const currentUserData = (usersData || {})[username];
    const userThemeColor = currentUserData?.appThemeColor || '#3b82f6';
    const userTextColor = currentUserData?.appTextColor || '#ffffff';
    const userOpacity = (currentUserData?.postBlurOpacity !== undefined ? currentUserData.postBlurOpacity : 50) / 100;

    const getExactModalBg = (color: string, opacityVal: number) => {
        if (!color) return `rgba(26, 26, 46, ${opacityVal})`;
        if (color.startsWith('rgba')) return color.replace(/[\d.]+\)$/, `${opacityVal})`);
        if (color.startsWith('rgb')) return color.replace('rgb', 'rgba').replace(')', `, ${opacityVal})`);
        let hex = color.replace('#', '');
        if (hex.length === 3 || hex.length === 4) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return (!isNaN(r) && !isNaN(g) && !isNaN(b)) ? `rgba(${r}, ${g}, ${b}, ${opacityVal})` : `rgba(26, 26, 46, ${opacityVal})`;
    };

    const modalBg = getExactModalBg(userThemeColor, userOpacity);

    const filteredIdeas = (Array.isArray(ideas) ? ideas : []).filter(idea => 
        (idea?.text || '').toLowerCase().includes(searchQuery.toLowerCase()) || 
        (idea?.category || '').toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div
            onClick={onClose}
            className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/80 animate-fade-in"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{ backgroundColor: modalBg, color: userTextColor }}
                className="relative rounded-[2.5rem] w-full max-w-md overflow-hidden flex flex-col border border-white/10 shadow-2xl animate-scale-up"
            >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-white/5">
                    <div>
                        <h2
                            className="text-xl font-black text-white tracking-tight italic uppercase"
                            style={{ color: userThemeColor, ...stickerTextStyle(0.15) }}
                        >
                            Idea Bank 🧠
                        </h2>
                        <p
                            className="text-[10px] text-gray-400 font-bold uppercase tracking-widest"
                            style={stickerTextStyle(0.18)}
                        >
                            Your logged thoughts & feature roadmaps
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-full hover:bg-white/10 transition-colors"
                    >
                        <XIcon className="w-6 h-6 text-white/70" />
                    </button>
                </div>

                {/* Search Bar */}
                <div className="px-6 pt-4 pb-2">
                    <div className="flex items-center bg-black/20 border border-white/10 rounded-2xl px-4 py-2">
                        <SearchIcon className="w-4 h-4 text-white/40 mr-2" />
                        <input
                            type="text"
                            placeholder="Search your mind..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="bg-transparent border-none outline-none text-xs w-full text-white placeholder-white/30"
                        />
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 max-h-[50vh] overflow-y-auto custom-scrollbar flex-grow">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="w-8 h-8 border-4 border-white/10 border-t-white/40 rounded-full animate-spin"></div>
                        </div>
                    ) : filteredIdeas.length === 0 ? (
                        <div className="text-center py-12 space-y-3">
                            <p className="text-xs text-white/40 font-bold uppercase tracking-wider">No thoughts logged yet</p>
                            <p className="text-[10px] text-white/30 max-w-[200px] mx-auto leading-relaxed">
                                Chat with GoodieBoy in your DMs. When you propose ideas, he will automatically log them here!
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {filteredIdeas.map((idea, index) => (
                                <div
                                    key={idea?.id || `idea-${index}`}
                                    className="bg-black/25 border border-white/5 rounded-3xl p-4 space-y-3 relative group"
                                >
                                    <div className="flex justify-between items-start">
                                        <span 
                                            className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border"
                                            style={{ 
                                                color: userThemeColor, 
                                                borderColor: `${userThemeColor}40`,
                                                backgroundColor: `${userThemeColor}10`
                                            }}
                                        >
                                            {idea?.category || 'Uncategorized'}
                                        </span>
                                        <button
                                            onClick={() => handleDelete(idea.id)}
                                            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-red-500/60 hover:text-red-500 hover:bg-white/5 rounded-lg"
                                        >
                                            <TrashIcon className="w-4 h-4" />
                                        </button>
                                    </div>
                                    <p className="text-xs text-white/90 leading-relaxed font-sans select-text">
                                        {idea?.text || ''}
                                    </p>
                                    <div className="flex items-center justify-between text-[8px] text-white/30 font-bold tracking-wider uppercase border-t border-white/5 pt-2">
                                        <span>Logged</span>
                                        <span>{idea?.timestamp ? new Date(idea.timestamp).toLocaleDateString() : ''}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
